import { type ConditionProperties } from 'json-rules-engine';
import {
  getGroupConditions,
  type RuleTopLevelCondition,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { type OptimizationEditorStep } from '@bbkAdminComponents/optimizer/OptimizationEditorStepEnum';
import {
  OptimizationActionType,
  type Optimization,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

export class OptimizationEditorHelper {
  static getNextButtonText(
    curStep: OptimizationEditorStep
  ): string | undefined {
    switch (curStep) {
      case 'audience':
        return 'Continue to Actions';
      case 'actions':
        return 'Continue to Configurations';
      case 'configurations':
        return 'Continue to Review';
      case 'review':
        return 'Publish Optimization';
      case 'publish':
        return undefined;
      default:
        return 'Continue';
    }
  }

  static getNextStep(
    curStep: OptimizationEditorStep
  ): OptimizationEditorStep | undefined {
    switch (curStep) {
      case 'audience':
        return 'actions';
      case 'actions':
        return 'configurations';
      case 'configurations':
        return 'review';
      case 'review':
        return 'publish';
      case 'publish':
        return undefined;
      default:
        return undefined;
    }
  }

  static getPrevStep(
    curStep: OptimizationEditorStep
  ): OptimizationEditorStep | undefined {
    switch (curStep) {
      case 'audience':
        return undefined;
      case 'actions':
        return 'audience';
      case 'configurations':
        return 'actions';
      case 'review':
        return 'configurations';
      case 'publish':
        return 'review';
      default:
        return undefined;
    }
  }

  private static isConditionComplete(condition: ConditionProperties): boolean {
    if (!condition.fact || !condition.operator) {
      return false;
    }
    // if operator is "is empty" or "is not empty", no value is needed
    if (
      condition.operator === 'Generic.isNilOrEmpty' ||
      condition.operator === 'Generic.isNotNilOrEmpty'
    ) {
      return true;
    }
    const value = condition.value;
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return !!value;
  }

  static isAudienceComplete(optimization: Partial<Optimization>): boolean {
    const audience = optimization.audience as RuleTopLevelCondition | undefined;
    if (!audience) return false;
    const conditions = getGroupConditions(audience);
    if (!conditions.length) return false;
    return conditions.every(OptimizationEditorHelper.isConditionComplete);
  }

  static areActionsComplete(optimization: Partial<Optimization>): boolean {
    const actions = optimization.actions;
    if (!actions || 0 >= actions.length) {
      return false;
    }
    return actions.every((action) => {
      if (
        !action.display_name ||
        action.display_name.trim() === '' ||
        !action.condition
      ) {
        return false;
      }
      const conditions = getGroupConditions(
        action.condition as RuleTopLevelCondition
      );
      if (!conditions.every(OptimizationEditorHelper.isConditionComplete)) {
        return false;
      }
      if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
        return !!action.adjustment_type && !!action.adjustment_value;
      }
      if (action.type === OptimizationActionType.PLAN_CHANGE) {
        return (
          action.plans.length > 0 &&
          action.plans.every(
            (p) =>
              !!p.current_plan &&
              !!p.target_plan &&
              p.current_plan !== p.target_plan
          )
        );
      }
      return true;
    });
  }

  static areConfigsComplete(optimization: Partial<Optimization>): boolean {
    const config = optimization.configurations;
    if (!config) return false;
    return !!config.mid_cycle_billing && !!config.scheduled_changes_handling;
  }

  static isStepValid(
    step: OptimizationEditorStep,
    optimization: Partial<Optimization>
  ): boolean {
    switch (step) {
      case 'audience':
        return OptimizationEditorHelper.isAudienceComplete(optimization);
      case 'actions':
        return OptimizationEditorHelper.areActionsComplete(optimization);
      case 'configurations':
        return OptimizationEditorHelper.areConfigsComplete(optimization);
      case 'review':
      case 'publish':
        return (
          OptimizationEditorHelper.isAudienceComplete(optimization) &&
          OptimizationEditorHelper.areActionsComplete(optimization) &&
          OptimizationEditorHelper.areConfigsComplete(optimization)
        );
      default:
        return false;
    }
  }

  /**
   * Determines the initial step for the optimization editor based on the optimization configuration.
   * The step is inferred from which fields are present:
   * - If audience is not present → 'audience'
   * - If audience is present but actions is not → 'actions'
   * - If actions is present but configurations is not → 'configurations'
   * - If configurations are present → 'review'
   */
  static getInitialStep(optimization: Optimization): OptimizationEditorStep {
    // Check if audience is present and non-empty
    // The data from getOptimizations may have these as JSON strings
    const hasAudience = optimization.audience;

    // Check if actions is present and non-empty
    const hasActions = optimization.actions;

    // Check if configurations is present and non-empty
    const hasConfigurations = optimization.configurations;

    if (!hasAudience) {
      return 'audience';
    }
    if (!hasActions) {
      return 'actions';
    }
    if (!hasConfigurations) {
      return 'configurations';
    }
    return 'review';
  }
}
